/**
 * Calendar-list alias membership: which events of the calendar a
 * calendar-list row shows, from the picker config persisted on the row
 * (`allSelected`, `categories`, `visibilityMode`, `customCalendars`,
 * `readOnly`) and the user-level calendar visibility a consumer injects.
 * The rules every renderer of the alias applies; reached through
 * `CalendarEventRead.peekListConfig` / `membership`, not a barrel.
 */
import { Json, type JsonObject, type Option } from "@tento-lona/sheets";
import type { EventItemAllDay, EventItemTimed } from "@tento-lona/sheets";
import { AuthScopedCalendarId } from "../events/calendar-types.ts";
import { EventCategory } from "../events/event-category-item.ts";

/**
 * A calendar-list row's picker config with its defaults applied
 * (`CalendarEventRead.peekListConfig`): all selected, default visibility,
 * no custom calendars and writable where the row says nothing.
 */
export interface CalendarListConfig {
  /** `"all"`: every category shows (the category filter is off); `"chosen"`: the picker's choice applies. */
  readonly selection: "all" | "chosen";
  /** `"custom"`: only `customCalendars` show; `"default"`: the user's own calendar visibility decides. */
  readonly visibilityMode: "default" | "custom";
  /** The calendars custom mode shows. */
  readonly customCalendars: readonly AuthScopedCalendarId[];
  /** The categories an item must carry one of; `null`: any (all selected, or none chosen). */
  readonly categories: Option<readonly EventCategory[]>;
  /** `"read-only"`: the row takes no writes (no create, no edit delegate). */
  readonly access: "editable" | "read-only";
}

/** The verdict of a config's membership rule (`calendarListVerdict`). */
export namespace CalendarListConfig {
  /**
   * Why the calendar-list row shows an item or not; consumers read it as
   * `CalendarEventRead.Verdict`. A non-member names the first rule that
   * excluded it, in the order membership applies them:
   * - `calendar-hidden`: default visibility, and the viewer hid its calendar;
   * - `not-in-custom-list`: custom visibility, and its calendar is not one of
   *   the row's custom calendars;
   * - `category-excluded`: its calendar is visible, but the row names
   *   categories and its category (`category`, `null` for none) is not one of
   *   them (`categories`).
   */
  export type Verdict =
    | { readonly member: true }
    | { readonly member: false; readonly reason: "calendar-hidden" | "not-in-custom-list"; readonly calendar: AuthScopedCalendarId }
    | {
      readonly member: false;
      readonly reason: "category-excluded";
      readonly category: Option<string>;
      readonly categories: readonly string[];
    };
}

/** The calendar-list picker config as persisted on the row: `null` where the bag is silent. */
export type CalendarListAttributes = {
  readonly allSelected: Option<boolean>;
  readonly categories: Option<readonly EventCategory[]>;
  readonly visibilityMode: Option<"default" | "custom">;
  readonly customCalendars: Option<readonly AuthScopedCalendarId[]>;
  readonly readOnly: Option<boolean>;
};

const VISIBILITY_MODES: readonly ("default" | "custom")[] = ["default", "custom"];

/**
 * Read the picker config off a calendar-list row's attribute bag. Categories
 * outside the vocabulary (`EventCategory.ALL`, kept in its order) and
 * malformed `[authId, calendarId]` pairs are dropped.
 */
export function decodeCalendarListAttributes(attributes: JsonObject): CalendarListAttributes {
  const categories = Json.strings(attributes, "categories");
  const calendars = Json.array(attributes, "customCalendars");
  return {
    allSelected: Json.boolean(attributes, "allSelected"),
    categories: categories == null ? null : EventCategory.ALL.filter((c) => categories.includes(c)),
    visibilityMode: Json.oneOf(attributes, "visibilityMode", VISIBILITY_MODES),
    customCalendars: calendars == null ? null : decodeCalendarIds(calendars),
    readOnly: Json.boolean(attributes, "readOnly"),
  };
}

function decodeCalendarIds(list: readonly Json[]): AuthScopedCalendarId[] {
  const out: AuthScopedCalendarId[] = [];
  for (const item of list) {
    if (!Array.isArray(item) || item.length !== 2) continue;
    const [authId, calendarId] = item;
    if (typeof authId !== "string" || typeof calendarId !== "string") continue;
    const acid = AuthScopedCalendarId.parse(authId, calendarId);
    if (acid != null) out.push(acid);
  }
  return out;
}

/**
 * The config with its defaults: all selected, default visibility, no custom
 * calendars, writable; categories filter only when not all selected and at
 * least one is chosen.
 */
export function calendarListConfig(attributes: Option<CalendarListAttributes>): CalendarListConfig {
  const allSelected = attributes?.allSelected ?? true;
  const chosen = attributes?.categories ?? [];
  return Object.freeze({
    selection: allSelected ? "all" : "chosen",
    visibilityMode: attributes?.visibilityMode ?? "default",
    customCalendars: attributes?.customCalendars ?? [],
    categories: allSelected || chosen.length === 0 ? null : chosen,
    access: attributes?.readOnly ?? false ? "read-only" : "editable",
  });
}

/**
 * Whether `item` is a member of the calendar-list row configured by
 * `config`: its calendar is visible — in custom mode, one of the custom
 * calendars; otherwise as the consumer's user-level `isCalendarVisible`
 * says — and, when the config names categories, its category is one of them.
 */
export function calendarListMember(
  config: CalendarListConfig,
  isCalendarVisible: (acid: AuthScopedCalendarId) => boolean,
): (item: EventItemTimed | EventItemAllDay) => boolean {
  const verdict = calendarListVerdict(config, isCalendarVisible);
  return (item) => verdict(item).member;
}

/** `calendarListMember` with its reason: the verdict for each item (`CalendarListConfig.Verdict`). */
export function calendarListVerdict(
  config: CalendarListConfig,
  isCalendarVisible: (acid: AuthScopedCalendarId) => boolean,
): (item: EventItemTimed | EventItemAllDay) => CalendarListConfig.Verdict {
  const categories: Option<readonly string[]> = config.categories;
  return (item) => {
    const acid = AuthScopedCalendarId.ofEvent(item.inner);
    if (config.visibilityMode === "custom") {
      if (!config.customCalendars.some((custom) => AuthScopedCalendarId.equals(custom, acid))) {
        return { member: false, reason: "not-in-custom-list", calendar: acid };
      }
    } else if (!isCalendarVisible(acid)) {
      return { member: false, reason: "calendar-hidden", calendar: acid };
    }
    if (categories == null || (item.category != null && categories.includes(item.category))) return MEMBER;
    return { member: false, reason: "category-excluded", category: item.category ?? null, categories };
  };
}

const MEMBER: CalendarListConfig.Verdict = Object.freeze({ member: true });
